import { Suspense, lazy, useEffect, useRef, useState } from "react";

/**
 * PERF — Punto de montaje diferido del panel de Maya.
 *
 * `ConciergeWidget` arrastra react-markdown + la lógica del motor de decisión
 * (~200 kB) y antes vivía en el bundle de entrada porque `__root.tsx` lo
 * importaba de forma estática, es decir: TODA visita a CUALQUIER página lo
 * descargaba y evaluaba antes de ser interactiva.
 *
 * Ahora se carga:
 *   1. Inmediatamente si el viajero pulsa Maya (evento `concierge:open`), o
 *   2. En tiempo libre del navegador (requestIdleCallback) para que la lógica
 *      proactiva y la memoria de conversación sigan funcionando igual.
 *
 * No cambia ningún comportamiento visible: el FAB propio del widget ya estaba
 * oculto y los únicos disparadores son BottomNav / DesktopDock vía evento.
 */
const ConciergeWidget = lazy(() =>
  import("./ConciergeWidget").then((m) => ({ default: m.ConciergeWidget })),
);

export function ConciergeMount() {
  const [mounted, setMounted] = useState(false);
  const autoOpenRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const activate = (fromUser: boolean) => {
      if (cancelled) return;
      if (fromUser) autoOpenRef.current = true;
      setMounted(true);
    };

    const onOpen = () => activate(true);
    window.addEventListener("concierge:open", onOpen);

    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (idle) idleId = idle(() => activate(false), { timeout: 4000 });
    else timeoutId = window.setTimeout(() => activate(false), 2500);

    return () => {
      cancelled = true;
      window.removeEventListener("concierge:open", onOpen);
      const cancelIdle = (window as unknown as { cancelIdleCallback?: (id: number) => void })
        .cancelIdleCallback;
      if (idleId !== undefined && cancelIdle) cancelIdle(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  if (!mounted) return null;

  return (
    <Suspense fallback={null}>
      <ConciergeWidget autoOpen={autoOpenRef.current} />
    </Suspense>
  );
}
